import {
  BellOutlined,
  BuildOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  FieldTimeOutlined,
  ScheduleOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { L } from '@lib';
import {
  DropOffDateStatus,
  DropOffDateStatusObjectKey,
  LocalizedServiceOrderStatus,
  LocalizedServiceOrderStatusObjectKey,
  OrderStatus,
  ServiceOrderStatusColor,
  ServiceOrderStatusColorObjectKey,
} from '@models';
import { NotificationService } from '@services';
import { formatDateWithOffset, isTimeTbc } from '@utils';
import { Alert, Button, DatePicker, Divider, InputNumber, Modal, Select, Tooltip, message } from 'antd';
import { SharedCard } from './SharedCard';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { useStore } from '@stores';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTimeRangeSelect } from '../Form/DateTimeRangeSelect';
import { ServiceHistoryModal } from '../Form/ServiceHistoryModal';
import { OrderStatusSelect } from '../Select';
import { MasterBuildTag } from '../Tag/MasterBuildTag';
import { AvailableWorkStationSelect } from '../Select/AvailableWorkStationSelect';
import { TechnicianSelect } from '../Select/TechnicianSelect';

// --- Styles ---

const FIELD_WIDTH = '62%';

const iconStyle = (color: string): React.CSSProperties => ({
  fontSize: 13,
  color,
  flexShrink: 0,
});

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 32,
  gap: 8,
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flex: 1,
  fontSize: 13,
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.65)',
};

const fieldStyle: React.CSSProperties = {
  width: FIELD_WIDTH,
  flexShrink: 0,
};

const valueStyle: React.CSSProperties = { fontSize: 13, color: 'rgba(0, 0, 0, 0.88)' };
const emptyValueStyle: React.CSSProperties = { fontSize: 13, color: 'rgba(0, 0, 0, 0.45)' };

// --- Time options ---

const timeOptions = Array.from({ length: 13 }, (_, hour) =>
  Array.from({ length: 4 }, (_, index) => {
    const h = ((hour + 8) % 24).toString().padStart(2, '0');
    const m = (index * 15).toString().padStart(2, '0');
    return { value: `${h}:${m}`, label: `${h}:${m}` };
  })
).flat();

// --- Sub-components ---

const FieldLabel = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <Tooltip title={text}>
    <span style={labelStyle}>
      {icon} {text}
    </span>
  </Tooltip>
);

const ReadOnlyValue = ({ value, fallback }: { value?: string | null; fallback: string }) => (
  <span style={value ? valueStyle : emptyValueStyle}>{value || fallback}</span>
);

const formatLoggedTime = (totalMinutes: number) => {
  const total = Math.max(0, Math.floor(totalMinutes));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

// Read-only display of the time the job has spent in the InProgress status. Counts up
// live while the job is in progress, otherwise shows the frozen accumulated total.
const LoggedTimeValue = ({
  accumulatedMinutes,
  startedAt,
  running,
}: {
  accumulatedMinutes: number;
  startedAt?: string;
  running: boolean;
}) => {
  const startedMs = running && startedAt ? dayjs(startedAt).valueOf() : null;
  const compute = () =>
    accumulatedMinutes + (startedMs ? Math.max(0, (Date.now() - startedMs) / 60000) : 0);
  const [minutes, setMinutes] = useState(compute);

  useEffect(() => {
    setMinutes(compute());
    if (!startedMs) return;
    const id = setInterval(() => setMinutes(compute()), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedMs, accumulatedMinutes]);

  return <ReadOnlyValue value={formatLoggedTime(minutes)} fallback="—" />;
};

// --- Types ---

export const JobDetailsCard = observer(() => {
  const { orderStore } = useStore();
  const { order } = orderStore;
  const [openMileageModal, setOpenMileageModal] = useState(false);
  const [notifying, setNotifying] = useState(false);

  if (!order) return null;

  const isLocked = order.status === OrderStatus.Paid || order.status === OrderStatus.Ready;
  const canEditDropOff = !!DropOffDateStatus[order.status as DropOffDateStatusObjectKey];

  const updateField = useCallback(
    (field: string, value: any) => {
      orderStore.updateOrderField({ [field]: value });
    },
    [orderStore]
  );

  // --- Drop-off helpers ---

  const dropOffDayjs = useMemo(
    () => (order.dropOffDate ? dayjs(order.dropOffDate) : dayjs()),
    [order.dropOffDate]
  );

  const handleDropOffDateChange = useCallback(
    (date: dayjs.Dayjs | null) => {
      if (!date) return;
      const h = dropOffDayjs.hour();
      const m = dropOffDayjs.minute();
      const updated = formatDateWithOffset(dayjs(date).hour(h).minute(m).second(0));
      updateField('dropOffDate', updated);
    },
    [dropOffDayjs, updateField]
  );

  const handleDropOffTimeChange = useCallback(
    (value: string) => {
      const [h, m] = value.split(':');
      const updated = formatDateWithOffset(
        dayjs(order.dropOffDate).hour(Number(h)).minute(Number(m)).second(0)
      );
      updateField('dropOffDate', updated);
    },
    [order.dropOffDate, updateField]
  );

  // --- Drop-off / job-date consistency warning ---

  // Drop-off is expected to fall on the same day as the job. When the job date is moved
  // (forward or backward), the drop-off can land on a different day and needs re-syncing.
  // The check compares the drop-off against the job date only (not "now"). We only warn
  // (nothing is blocked or required) and offer a one-click sync.
  const dropOffOutOfSync = useMemo(() => {
    if (!order.startAt || !order.dropOffDate) return false;
    return !dayjs(order.dropOffDate).isSame(dayjs(order.startAt), 'day');
  }, [order.startAt, order.dropOffDate]);

  // Aligns the drop-off with the job date, keeping its time-of-day but never letting it
  // land after the job start.
  const handleSyncDropOffToJob = useCallback(() => {
    if (!order.startAt) return;
    const jobStart = dayjs(order.startAt);
    const current = order.dropOffDate ? dayjs(order.dropOffDate) : jobStart;
    let synced = jobStart.hour(current.hour()).minute(current.minute()).second(0);
    // A TBC job starts "sometime that day": the drop-off keeps its time rather than being
    // pulled to midnight.
    if (!isTimeTbc(order.startAt) && synced.isAfter(jobStart)) synced = jobStart;
    updateField('dropOffDate', formatDateWithOffset(synced));
  }, [order.startAt, order.dropOffDate, updateField]);

  // --- Handlers ---

  const handleStatusChange = useCallback(
    (status: string) => {
      if (status === OrderStatus.Completed) {
        setOpenMileageModal(true);
      } else if (status === 'Canceled') {
        // Three-button modal: [Go back] cancels the action; [No] cancels the order without restoring inventory; [Yes] cancels and returns parts to stock.
        const modal = Modal.confirm({
          title: 'Cancel this order — return parts to stock?',
          content:
            'The order will be cancelled. Should any inventory parts used on this job be returned to stock?',
          icon: null,
          width: 520,
          okText: 'Yes',
          okButtonProps: { type: 'primary' },
          cancelText: 'No',
          onOk: () => {
            updateField('status', status);
            updateField('restoreInventory', true);
          },
          onCancel: () => {
            updateField('status', status);
            updateField('restoreInventory', false);
          },
          footer: (_, { OkBtn, CancelBtn }) => (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => modal.destroy()}>Go back</Button>
              <CancelBtn />
              <OkBtn />
            </div>
          ),
        });
      } else {
        updateField('status', status);
      }
    },
    [updateField]
  );

  const handleNotifyCustomer = useCallback(async () => {
    if (!order.customer?.userId) {
      message.warning('Customer is not an app user.');
      return;
    }
    setNotifying(true);
    try {
      await NotificationService.create({
        isInitial: false,
        sendPush: true,
        orderId: order.id,
        dropOffDate: String(order.dropOffDate || ''),
      });
      message.success('Customer notified');
    } catch {
      message.error('Failed to notify customer');
    } finally {
      setNotifying(false);
    }
  }, [order.id, order.customer?.userId, order.dropOffDate]);

  // --- Render ---

  return (
    <SharedCard style={{ height: '100%' }}>
      {/* Build - only shown when this job is one leg of a build */}
      {order.masterBuildId && (
        <div style={rowStyle}>
          <FieldLabel icon={<BuildOutlined style={iconStyle('#722ed1')} />} text="Build" />
          <div style={fieldStyle}>
            <MasterBuildTag order={order} />
          </div>
        </div>
      )}

      {/* Status */}
      <div style={rowStyle}>
        <FieldLabel icon={<CheckCircleOutlined style={iconStyle('#52c41a')} />} text="Status" />
        <div style={fieldStyle}>
          {isLocked ? (
            <span
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                background:
                  ServiceOrderStatusColor[order.status as ServiceOrderStatusColorObjectKey] ||
                  '#f0f0f0',
                color: 'rgba(0, 0, 0, 0.75)',
              }}
            >
              {LocalizedServiceOrderStatus[order.status as LocalizedServiceOrderStatusObjectKey] ||
                order.status}
            </span>
          ) : (
            <OrderStatusSelect
              hideFirstOption
              hideApproval
              hidePaid={order.status !== OrderStatus.Paid}
              hideReady={order.status !== OrderStatus.Ready}
              style={{ width: '100%' }}
              status={order.status}
              onChange={handleStatusChange}
            />
          )}
        </div>
      </div>

      <Divider style={{ margin: '13px 0' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Work station */}
        <div style={rowStyle}>
          <FieldLabel icon={<ToolOutlined style={iconStyle('#722ed1')} />} text="Work station" />
          <div style={fieldStyle}>
            {!isLocked ? (
              <AvailableWorkStationSelect
                orderId={order.id}
                startAt={order.startAt}
                endAt={order.endAt}
                disabled={isLocked}
                style={{ width: '100%' }}
                value={order.workStationId}
                defaultValue={
                  order.workStation
                    ? [{ label: order.workStation.name, value: order.workStationId }]
                    : undefined
                }
                onChange={(val) => updateField('workStationId', val)}
              />
            ) : (
              <ReadOnlyValue value={order.workStation?.name} fallback="Not assigned" />
            )}
          </div>
        </div>

        {/* Technician */}
        <div style={rowStyle}>
          <FieldLabel icon={<UserOutlined style={iconStyle('#1677ff')} />} text="Technician" />
          <div style={fieldStyle}>
            {!isLocked ? (
              <TechnicianSelect
                style={{ width: '100%' }}
                placeholder="Select technician"
                value={order.userId ? Number(order.userId) : undefined}
                disabled={isLocked}
                defaultValue={
                  order.user
                    ? [{ label: order.user.fullname, value: Number(order.userId) }]
                    : undefined
                }
                onClear={() => updateField('userId', undefined)}
                onSelect={(id) => updateField('userId', id)}
                onDeselect={() => updateField('userId', undefined)}
              />
            ) : (
              <ReadOnlyValue value={order.user?.fullname} fallback={L('Not Assigned')} />
            )}
          </div>
        </div>

        {/* Mileage */}
        <div style={rowStyle}>
          <FieldLabel icon={<DashboardOutlined style={iconStyle('#fa8c16')} />} text="Mileage" />
          <div style={fieldStyle}>
            {!isLocked ? (
              <InputNumber
                placeholder="Enter mileage"
                style={{ width: '100%' }}
                controls={false}
                value={order.mileage}
                min={0}
                max={1000000}
                formatter={(val) => (val ? `${val}`.replace(/\./g, '') : '')}
                parser={(val) => (val ? Number(val.replace(/\./g, '')) : '') as any}
                onChange={(val) => updateField('mileage', val ?? undefined)}
              />
            ) : (
              <ReadOnlyValue
                value={order.mileage ? `${order.mileage.toLocaleString()} mls` : undefined}
                fallback="—"
              />
            )}
          </div>
        </div>

        {/* Job date & time */}
        <div style={rowStyle}>
          <FieldLabel icon={<ScheduleOutlined style={iconStyle('#2f54eb')} />} text="Job date" />
          <div style={fieldStyle}>
            {!isLocked ? (
              <DateTimeRangeSelect
                disabled={isLocked}
                dateRange={{ startAt: order.startAt, endAt: order.endAt }}
                setDateRange={(range) =>
                  orderStore.updateOrderField({ startAt: range.startAt, endAt: range.endAt })
                }
              />
            ) : (
              <ReadOnlyValue
                value={
                  !order.startAt
                    ? undefined
                    : isTimeTbc(order.startAt)
                      ? `${dayjs(order.startAt).format('ddd, MMM DD')} · ${L('TBC')}`
                      : `${dayjs(order.startAt).format('ddd, MMM DD')} ${dayjs(order.startAt).format('HH:mm')} - ${dayjs(order.endAt).format('HH:mm')}`
                }
                fallback={L('Not Assigned')}
              />
            )}
          </div>
        </div>

        {/* Actual worked time */}
        <div style={rowStyle}>
          <FieldLabel
            icon={<ClockCircleOutlined style={iconStyle('#13c2c2')} />}
            text="Actual worked time"
          />
          <div style={fieldStyle}>
            {!isLocked ? (
              <InputNumber
                placeholder="Minutes"
                style={{ width: '100%' }}
                controls={false}
                value={order.actualWorkedTime}
                min={0}
                onChange={(val) => updateField('actualWorkedTime', val ?? undefined)}
              />
            ) : (
              <ReadOnlyValue
                value={order.actualWorkedTime != null ? `${order.actualWorkedTime} min` : undefined}
                fallback="—"
              />
            )}
          </div>
        </div>

        {/* Drop-off date */}
        <div style={rowStyle}>
          <FieldLabel icon={<CalendarOutlined style={iconStyle('#f5222d')} />} text="Drop-off" />
          <div style={fieldStyle}>
            {!isLocked && canEditDropOff ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                <DatePicker
                  inputReadOnly
                  showTime={false}
                  allowClear={false}
                  disabledDate={(current) => current < dayjs().startOf('day')}
                  format="DD/MM"
                  style={{ flex: 1, minWidth: 0, height: 32 }}
                  value={dropOffDayjs}
                  onChange={handleDropOffDateChange}
                />
                <Select
                  style={{ flex: 1, minWidth: 0, height: 32 }}
                  options={timeOptions}
                  value={dropOffDayjs.format('HH:mm')}
                  onChange={handleDropOffTimeChange}
                />
              </div>
            ) : (
              <ReadOnlyValue
                value={
                  order.dropOffDate
                    ? dayjs(order.dropOffDate).format('YYYY-MM-DD, HH:mm')
                    : undefined
                }
                fallback="—"
              />
            )}
          </div>
        </div>

        {/* Drop-off / job-date out-of-sync warning (non-blocking) */}
        {!isLocked && dropOffOutOfSync && (
          <Alert
            type="error"
            showIcon
            style={{ fontSize: 12 }}
            title="The drop-off date does not match the job date."
            action={
              canEditDropOff ? (
                <Button size="small" type="primary" onClick={handleSyncDropOffToJob}>
                  Sync drop-off
                </Button>
              ) : undefined
            }
          />
        )}

        {/* Logged time (time spent in progress) */}
        <div style={rowStyle}>
          <FieldLabel
            icon={<FieldTimeOutlined style={iconStyle('#0a7d28')} />}
            text="Logged time"
          />
          <div style={fieldStyle}>
            <LoggedTimeValue
              accumulatedMinutes={order.inProgressMinutes ?? 0}
              startedAt={order.inProgressStartedAt}
              running={order.status === OrderStatus.InProgress}
            />
          </div>
        </div>
      </div>

      {/* Notify button */}
      {!isLocked && canEditDropOff && order.customer?.userId && (
        <div style={{ marginTop: 30 }}>
          <Tooltip title="Sends a push notification to the customer about the drop-off date">
            <Button
              block
              icon={<BellOutlined />}
              onClick={handleNotifyCustomer}
              loading={notifying}
              style={{ backgroundColor: '#fffbe6', borderColor: '#fadb14', color: '#8c6d1f' }}
            >
              Notify app user of drop-off change
            </Button>
          </Tooltip>
        </div>
      )}

      {/* Mileage modal */}
      {openMileageModal && (
        <ServiceHistoryModal
          orderId={order.id}
          customerId={order.customerId}
          vin={order.vin}
          mileage={order.mileage}
          open={openMileageModal}
          setOpen={setOpenMileageModal}
          onCancel={() => setOpenMileageModal(false)}
        />
      )}
    </SharedCard>
  );
});
